/**
 * English fallbacks for message ids this front-end introduced.
 *
 * Translations are fetched from the platform, so an id the backend has not shipped yet
 * renders as its own id — "filter__sheet_apply" in the middle of a button. These are
 * merged *under* the fetched translations in LanguageProvider, so the platform always
 * wins and this only ever fills a hole.
 *
 * The rule that goes with this file: a new id gets an entry here in the same commit
 * that introduces it. An id that already exists in the platform's translations does not
 * belong here — reuse the existing one instead of adding a synonym.
 */
export const DEFAULT_MESSAGES: Record<string, string> = {
  // Home
  home__live_now: "{count} live now",

  // Filter sheets
  filter__sheet_apply: "Apply",
  filter__sheet_clear: "Clear",
  filter__sheet_close: "Close without changing anything",
  filter__sheet_back: "Back to more filters",
  filter__selected_count: "{count} selected",
  filter__more_title: "More filters",
  filter__more_subtitle:
    "{count, plural, =0 {Thermometer and gender stay on the feed} one {# on · thermometer and gender stay on the feed} other {# on · thermometer and gender stay on the feed}}",
  filter__search_language: "Search a language",
  filter__subtitle_thermometers: "Several can be on at once",
  filter__subtitle_genders: "Several can be on at once",
  filter__subtitle_age_ranges: "Several can be on at once",
  filter__subtitle_languages: "Several can be on at once",
  filter__subtitle_website_uses: "What members say they're here for",
  filter__subtitle_relationship_sought: "What members are looking for",
  filter__subtitle_has_fanclub: "Paid content behind a subscription",
  filter__any_thermometer: "Any heat",
  filter__any_gender: "Anyone",
  filter__any_age: "Any age",
  filter__any_language: "Any language",
  filter__any_website_use: "Any use of the site",
  filter__any_relationship: "Any relationship",
  filter__any_fanclub: "Free and paid",
  filter__explain_thermometer_soft: "Suggestive, nothing shown",
  filter__explain_thermometer_hot: "Partial nudity",
  filter__explain_thermometer_hard: "Nothing held back · needs a verified account",
  filter__explain_fanclub_yes: "Members who sell access to their snaps",
  filter__explain_fanclub_no: "Everything they post is free",

  // Appearance and vibe
  parameters__appearance: "Appearance",
  appearance__label: "Appearance",
  appearance__light: "Light",
  appearance__dark: "Dark",
  appearance__system: "System",
  vibe__label: "Vibe",
  vibe__sharp: "Sharp",
  vibe__default: "Default",
  vibe__soft: "Soft",
  vibe__explain: "How rounded everything is, across the whole site.",

  // Profile blocks
  profile__online_now: "Online now",
  profile__content: "Content",
  profile__album: "Album",
  profile__album_empty: "Nothing here yet.",
  profile__fanclub_only: "Fan club only",
  profile__fanclub_member: "You're a member",
  profile__fanclub_pitch: "Permanent photos and videos, kept for members of the club.",
  profile__fanclub_flames: "Flames needed",
  profile__fanclub_messages: "Messages needed",
  profile__fanclub_how: "How to join",
  profile__arrange: "Arrange my profile",
  profile__arrange_done: "Done",
  profile__arrange_hint:
    "Reorder your blocks and choose which ones visitors see. Identity always comes first.",
  profile__arrange_more: "More blocks are on the way",
  profile__block_pinned: "Always shown, always first",
  profile__block_identity: "Identity",
  profile__block_identity_hint: "Cover, avatar, name, bio",
  profile__block_highlights: "Highlights",
  profile__block_highlights_hint: "The sections of your profile",
  profile__block_fanclub_hint: "Your club and how to join it",
  profile__block_about_hint: "What you're looking for",
  profile__block_content_hint: "Snaps, album and fan club",

  // The content block's two tabs (29). Tab labels reuse the platform's own ids
  // (profile__photo, profile__album, profile__display_video, profile__fanclub).
  profile__filter_recent: "Recent",
  profile__ephemes_live:
    "{count, plural, one {# still live. Open it and it leaves the grid.} other {# still live. Open one and it leaves the grid.}}",
  profile__ephemes_live_own: "{count, plural, one {# live} other {# live}}",
  profile__ephemes_looks: "{views, number} looks taken",
  ephemeviewer__hours_left: "{hours} h left",

  // Trophies and relations, back as blocks (31). Titles reuse trophies__title and
  // profile__most_active_relationship.
  profile__block_trophies_hint: "Your trophies and the boost they give",
  profile__block_relations_hint: "Who's most active with you",
  profile__trophies_of: "{achieved} of {total}",
  profile__trophies_boost: "Trophies give {member} +{boost}% boost",
  profile__trophies_boost_own: "Your trophies give you +{boost}% boost",
  profile__relations_top: "Top {count}",
  profile__relations_show_all: "Show all {count}",
  trophy__boost_value: "+{boost}% boost",
  trophy__boost_hint: "Every flame sent to {member} counts for more",
  trophy__boost_hint_own: "Every flame sent to you counts for more",

  // The pair's number on the profile (31d) — the breakdown behind it reuses the
  // commonboost popin's shipped ids.
  profile__common_boost_together: "+{boost}% together",
  profile__common_boost_with: "Your boost with {member}",

  // Gift sheet (26). The flow's own strings reuse the chat tray's platform ids
  // (gifttray__header_common_boost, giftmessage__*); these fill only what the
  // profile entry point adds.
  gifttray__title: "Send a gift",
  gifttray__subtitle: "To {member}",
  gifttray__balance: "{count, number} tokens",
  gifttray__cost_after: "{cost, number} tokens · {left, number} left after",
  gifttray__back: "Back",
  gifttray__done: "Done",
  gifttray__sent_summary: "+{flames, number} flames for {member} · {left, number} tokens left.",
  gifttray__sent_rank: "Now #{rank, number}.",
  gifttray__sent_message: "You sent a gift",

  // The You page. Destinations reuse the menu's own ids (menu__my_profile,
  // menu__my_epheme, menu__my_settings, …); these are only what the hub adds.
  you__title: "You",
  you__activity:
    "<b>{visits, plural, one {# visit} other {# visits}}</b> and <b>{likes, plural, one {# like} other {# likes}}</b> since your last look",
  you__verification: "Verification",
  you__verification_progress: "{done} of {total} done",
  you__verification_done: "Done",
  you__verification_todo: "Not done yet",
  you__not_started: "Not started",
  you__start: "Start",
  you__age_verification: "Age verification",
  you__age_verification_note:
    "Without it you cannot see or post sexy and explicit snaps.",
  you__certification_note:
    "A photo holding a sign with your username. Certified profiles carry the badge and rank higher.",
  you__contributor_note:
    "An identity document, required before you can be paid or run a fan club. Stored by our payment provider, never shown on your profile.",
  you__view_as_visitor: "View as visitor",
  you__recent_snaps: "Recent snaps",
  you__see_all: "See all",
  you__help_legal: "Help and legal",
  you__get_help: "Get help",
  you__legal: "Legal",

  // The composer's fork and details screens. The rest of the flow reuses the
  // uploader's shipped ids (uploader__soft, uploader__recipients, …).
  uploader__pick_hint: "Photos and videos — up to {max} at once for an album post, one for a snap.",
  uploader__wrong_type: "Choose photos or videos.",
  uploader__too_big: "A file is too big — 300 MB at most.",
  uploader__kind_title: "New post",
  uploader__kind_items: "{count, plural, one {# item} other {# items}}",
  uploader__kind_add_hint: "Add another and it becomes an album post — a snap is one media.",
  uploader__kind_snap_title: "Snap",
  uploader__kind_album_title: "Album",
  uploader__kind_snap_single: "One photo or one video, on its own",
  uploader__kind_snap_once: "Each member can open it once",
  uploader__kind_snap_feed: "Shows in the feed and on your profile",
  uploader__kind_snap_expires: "Deleted automatically after 30 days — sooner if you delete it",
  uploader__kind_snap_challenge: "The only kind that can enter a challenge",
  uploader__kind_snap_disabled: "A snap is one media — remove down to one item to post one.",
  uploader__kind_album_batch: "One to ten items, each posted to your album",
  uploader__kind_album_stays: "Stays until you delete it",
  uploader__kind_album_profile: "Seen on your profile only — never in the feed",
  uploader__continue_snap: "Continue as a snap",
  uploader__continue_album: "Continue as an album post",
  uploader__change_media: "Change media",
  uploader__before_post: "Before you post",
  uploader__epheme_note:
    "A snap gives each member one look and deletes itself after 30 days. Anything you want kept belongs in the album.",
  uploader__album_hint: "Up to {max} items, posted together to your album.",
  uploader__album_note:
    "Album posts stay on your profile until you delete them, and never appear in the feed.",
  uploader__album_partial: "{count, plural, one {# item was} other {# items were}} posted before the error.",
  uploader__post_snap: "Post the snap",
  uploader__post_album: "Post {count, plural, one {# item} other {# items}}",

  // The rebuilt viewer. Everything else it says reuses shipped ids
  // (ephemeitem__thermometer_*, reportpopin__*, memberitem__follow, menu__login).
  ephemeviewer__end_title: "End of this batch",
  ephemeviewer__end_more: "That's everything you opened with — there are more waiting.",
  ephemeviewer__end_done: "You've seen everything that matches. Widen the filters to keep going.",
  ephemeviewer__load_more: "Load {count} more",
  ephemeviewer__days_left: "{days} d left",
  ephemeviewer__scroll_next: "Scroll for the next",
  ephemeviewer__reply: "Reply",
  ephemeviewer__gift: "Gift",
  ephemeviewer__profile: "Profile",
  ephemeviewer__up_next: "Next",
  ephemeviewer__keys_move: "move",
  ephemeviewer__keys_like: "like",
  ephemeviewer__keys_close: "close",
  ephemeviewer__chat_guest: "Log in to open the chat",

  // The member card set (34): the band's action pair. "Message" opens the chat;
  // where chat is refused the button degrades to a locked state instead of vanishing.
  memberitem__message: "Message",
  memberitem__no_chat: "No chat",

  // The comment thread on contents — the album is public and permanent, so it
  // carries comments where an epheme carries a chat. Endpoints not shipped yet.
  ephemeviewer__comments_count: "{count, plural, one {# comment} other {# comments}}",
  ephemeviewer__comments_sort_top: "Top",
  ephemeviewer__comments_sort_recent: "Recent",
  ephemeviewer__comment_placeholder: "Add a comment…",
  ephemeviewer__comments_empty: "Be the first to comment.",
  ephemeviewer__comment_author: "AUTHOR",
  ephemeviewer__comment_replying_to: "Replying to {name}",

  // The Top tab on Our members (35) and its place control (36). Gender pills and
  // continent names reuse shipped ids (gender_sought__label__*, continent__*).
  top__seg_all_time: "All time",
  top__seg_today: "Today",
  top__place_label: "Place",
  top__place_everywhere: "Everywhere",
  top__place_all_of: "All of {continent}",
  top__place_countries_count: "{count, plural, one {# country} other {# countries}}",
  top__place_ranking_count: "Ranking {count, plural, one {# country} other {# countries}}",
  top__place_show: "Show {place}",
  top__place_subtitle: "Ranking is per country set",

  // The chat overhaul (1a Column + 2a rail). Everything else the thread says reuses
  // shipped ids (textbar__placeholder, chatangel__*, chaterror__label__*,
  // chatlist__public_chat__*, chat__media_send, giftmessage__*, tipmessage__*).
  chat__you: "You",
  chat__today: "Today",
  chat__online_count: "{count, number} online",
  chat__new_messages: "{count, number} new",
  chat__paused_new: "Paused — {count, number} new",
  chat__room_placeholder: "Write to the room",
  chat__panel_title: "Chats",
  chat__panel_search: "Search",
  chat__panel_private: "Private",
  chat__panel_rooms: "Rooms",
  chat__open_page: "Open the chat page",
  chatmessage__blocked: "Hidden — you blocked this member",
  chatmessage__gift_sent: "You sent a gift · +{flames, number} flames",
  chatmessage__gift_received: "{member} sent you a gift · +{flames, number} flames",
  chatmessage__tip_sent: "You sent {amount, number} tokens · +{flames, number} flames",
  chatmessage__tip_received: "{member} sent you {amount, number} tokens · +{flames, number} flames",
  chatmessage__pending_view_once: "Tap to view once",
  chatmessage__kind_photo: "Photo",
  chatmessage__kind_video: "Video",
  chatmessage__uploading: "Uploading",
  chatmessage__upload_failed: "This media couldn't be sent",
  chatmessage__retry: "Retry",

  // The member actions sheet (3b) — tag, report and block keep their endpoints;
  // gender and age labels reuse shipped ids (gender_sought__label__*,
  // age_range__value_label__*).
  chatmember__tag_room: "Tag in the room",
  chatmember__message_privately: "Message privately",
  chatmember__view_profile: "View profile",
  chatmember__report: "Report to moderation",
  chatmember__block: "Block {member}",
  chatmember__unblock: "Unblock {member}",
  chatmember__block_note: "Hides their messages here and closes your private chat",

  // The chat text size setting, next to appearance in Parameters.
  chat_text__label: "Chat text size",
  chat_text__standard: "Standard",
  chat_text__large: "Large",
  chat_text__larger: "Larger",

  // The header search popup (Search propositions, 1a).
  searchpopup__placeholder: "Search a username",
  searchpopup__scope_filters: "In your filters",
  searchpopup__scope_all: "Everywhere",
  searchpopup__ignore: "Ignore",
  searchpopup__use_filters: "Filter",
  searchpopup__recent: "Recent searches",
  searchpopup__clear: "Clear",
  searchpopup__remove_recent: "Remove",
  searchpopup__see_in_feed: "See “{query}” in the members feed",
  searchpopup__close: "Close search",

  // The popin shell rebuild (Popups redesigned board). Everything a popin says
  // that the platform does not already ship a line for.
  popin__not_now: "Not now",
  popin__close: "Close",

  // The two payment flows' shared machine: step titles, the pinned footer's
  // running total, the card list, and honest processing / decline states.
  payment__step_title: "Payment",
  payment__phone_title: "Pay by phone",
  payment__total_today: "Total today",
  payment__other_way: "Pay another way",
  payment__pay_with: "Pay with",
  payment__card_declined_mark: "declined",
  payment__processing: "Confirming with your bank",
  payment__processing_hint: "Do not close this window",
  payment__declined_generic: "Your bank declined this payment",
  payment__declined_hint:
    "Nothing was charged. Try another card, or ask your bank why — some block payments to adult services by default.",
  payment__phone_partner_note:
    "Billed by your mobile operator. Follow the steps below — this panel is run by the payment partner.",

  // The platform ships no line for paymentrequired's body in the reference set;
  // this is the board's sentence, and the platform's own wins if one arrives.
  paymentrequired__new_desc:
    "Verifying your identity is free, but it is only open to members who have paid at least once. Either route works.",

  // The email gate names what it is unlocking, and can resume the blocked request.
  emailconfirm__confirmed_button: "I've confirmed",
  emailverifypage__change_email: "Change my email",
  emailconfirm__not_confirmed_yet: "Still not confirmed — open the link in the email first.",
  emailconfirm__unlock_tokens: "Confirm it to buy tokens",
  emailconfirm__unlock_subscribe: "Confirm it to subscribe",

  // Age verification as one counted flow (underage → pickvalidator → addcard).
  agegate__title: "Verify your age",
  agegate__step1_heading: "This content is for verified adults",
  agegate__step1_body:
    "The law requires us to check your age once before showing explicit content. It takes about a minute and you only do it a single time.",
  agegate__reassure_partner: "Checked by an accredited third party",
  agegate__reassure_private: "Never shown to other members",
  agegate__reassure_deleted: "Your document is deleted after the check",
  agegate__step2_heading: "How would you like to prove it?",
  agegate__continue: "Continue",
  agegate__verify_me: "Verify me",
  agegate__option_id: "Identity document",
  agegate__option_id_hint: "Photograph your ID with the camera · under a minute",
  agegate__option_card: "Bank card",
  agegate__option_card_hint: "Instant · no charge is made, ever",
  agegate__option_sub: "Already subscribed?",
  agegate__option_sub_hint: "A paid subscription verifies you automatically",
  agegate__partner_note: "Handled by our verification partner. We never see the document itself.",
  agegate__step3_heading: "Add your card",
  agegate__no_charge: "No charge is made. The card is used only to confirm you are over eighteen.",

  // The blocked-member card says what unblocking does before the button.
  blocked__title: "You blocked this member",
  blocked__keep_button: "Keep blocked",
  blocked__unblock_hint: "Unblocking lets them see your profile and write to you again.",

  // Chat media gets a titled frame instead of a floating image.
  chatcontent__title: "Private media",

  // The blocking upload card explains itself.
  uploading__keep_open: "Keep this window open until it finishes. You can carry on browsing afterwards.",

  // A missing thermometer is field validation now, not a popup.
  ephemeuploader__temp_missing: "Pick a thermometer level before you publish",

  // Shared chrome (toasts, locale, the guest hub, the email strip).
  toast__reply: "Reply",
  toast__retry: "Retry",
  locale__label: "Language",
  you__group_discover: "Discover",
  you__group_settings: "Settings",
  you__guest_title: "Create your account",
  you__guest_body: "Post your ephemerals, chat, and see who visited you.",
  you__guest_signup: "Sign up",
  emailbanner__text: "Confirm your e-mail address to unlock messages and chat.",
  emailbanner__confirm: "Confirm",
  emailbanner__resend: "Resend the e-mail",
  emailbanner__sent: "E-mail sent to {email}. Check your inbox.",

  // The tokens history table finally speaks the locale.
  mytokens__table_date: "Date",
  mytokens__table_operation: "Operation",
  mytokens__table_amount: "Amount",

  // The online filter, back from master (RF8).
  filter__title_online: "Online",
  filter__subtitle_online: "Show only members who are connected right now.",
  filter__any_online: "Everyone, online or not",
  filter__online_yes: "Online now",
  filter__online_no: "Offline"
};

export default DEFAULT_MESSAGES;
